<template>
  <div id="app" :class="{ 'wcag-mode-on': !isNormalMode }">
    <router-view></router-view>

    <SnackBar v-if="closeAccountSnackbar" @close="setCloseAccountSnackbar(false)" @tap="setCloseAccountSnackbar(false)">
      {{ $t("CloseAccount.Snackbar.success") }}
    </SnackBar>

    <!-- landscape for mobile is not allowed -->
    <LandscapeAlert v-if="isMobile" />

    <!-- favorite prompt for all views can use it to save code size -->
    <transition name="zero-fade">
      <FavoritePrompt v-if="favoritePrompt" />
    </transition>

    <!-- search current place map clicked -->
    <Model20210519 v-if="showSearchAlertUI" :title="$t('Top.hinanModal')" @close-clicked="closeSearchAlertUI">
      <TopViewHinanjoSearchContent :btn-list="modalButtons" @btn-clicked="modalBtnClicked" />
    </Model20210519>

    <NudgeMessage
      v-if="isContrastNudgeShown"
      status="wcag"
      :message="$t('ColorContrast.nudgeTitle')"
      :timeout="99999"
      width="359px"
      height="60px"
      position="bottom"
      @clicked="clickContrastNudge"
      @close="closeContrastNudge"
    />

    <!-- browser言語とURL言語と違う場合 -->
    <LanguageSelector />
  </div>
</template>

<script lang="ts">
import { mapGetters, mapMutations, mapState } from "vuex";
import New3MapTypeBtnMixin from "@/mixins/new-3-map-type-btn";
import VueTypedMixin from "vue-typed-mixins";
import { setCookie, getCookie } from "@/util/cookie";
import { isWcagMode } from "@/util/wcag";
import { setVh } from "./util/mobile";

export default VueTypedMixin(New3MapTypeBtnMixin).extend({
  name: "app",
  mixins: [New3MapTypeBtnMixin],
  components: {
    LandscapeAlert: () => import(/* webpackChunkName: "landscape-alert" */ "@/components/landscape-alert.vue"),
    FavoritePrompt: () => import("@/components/favorite-prompt.vue"),
    Model20210519: () => import("@/components/common/model-20210519.vue"),
    TopViewHinanjoSearchContent: () => import("@/components/top-view/top-view-hinanjo-search-content.vue"),
    NudgeMessage: () => import("@/components/common/nudge-message.vue"),
    LanguageSelector: () => import("@/components/language/language-selector.vue"),
    SnackBar: () => import("@/components/common/snack-bar.vue"),
  },

  data: () => ({
    isNormalMode: true,
    isContrastNudgeShown: true,
  }),

  computed: {
    ...mapState("ui", ["favoritePrompt", "showSearchAlertUI", "closeAccountSnackbar"]),
    ...mapState("data", ["hasCurrentLocation"]),
    ...mapGetters("ui", ["isMobile"]),
  },

  watch: {
    gpsButtonStatus(newVal?: boolean) {
      console.log(`watch gpsButtonStatus at app.vue: ${newVal}`);
      this.modalButtons[0].status = this.gpsButtonStatus;
    },

    hasCurrentLocation(newVal?: boolean) {
      console.log(`watch CurrentLocation at app.vue: ${newVal}`);
      if (newVal) {
        this.gpsButtonStatus = "normal";
      } else {
        this.gpsButtonStatus = "error";
      }
    },

    $route(_to, from) {
      if (from.path === "/") this.setCloseAccountSnackbar(false);
    },
  },

  created() {
    this.isNormalMode = !isWcagMode();
    this.isContrastNudgeShown = getCookie("color-mode") === undefined;
    // to set because most of previous codes using it
    this.$store.commit("data/setLanguage", this.$i18n.locale);
    this.resetPersistVuex();
  },

  mounted() {
    // reset 100vh issue
    window.addEventListener("resize", setVh);
    setVh();
  },

  beforeDestroy() {
    window.removeEventListener("resize", setVh);
  },

  methods: {
    ...mapMutations("ui", ["setShowSearchAlertUI", "setCloseAccountSnackbar"]),
    /** @remarks first visit */
    resetPersistVuex() {
      this.$store.commit("ui/setAlertView", {
        status: false,
        msg: "",
        btnTitle: "",
      });
      this.$store.commit("ui/isFetchingError", false);
    },

    closeContrastNudge() {
      this.isContrastNudgeShown = false;
      setCookie("color-mode", "normal", 400);
    },

    clickContrastNudge() {
      this.isContrastNudgeShown = false;
      this.$router.push("/wcag-selection");
    },
  },
});
</script>

<style lang="scss">
@import "@/assets/css/transition.scss";
@import "@/assets/css/fonts.scss";
@import "@/assets/css/material-icons.scss";

/** dynamic colors for wcag mode of web a11y */
:root {
  --spooky-ghost: #{$spooky-ghost};
  --lime: #{$lime};
  --mint: #{$mint};
  --important: #{$important};
  --orange: #{$orange};
  --tiger: #{$tiger};
  --cheek: #{$cheek};
  --coral: #{$coral};
  --link: #{$link};
  --tsuna: #{$tsuna};
}

#app.wcag-mode-on {
  --spooky-ghost: #{$wcag_spooky-ghost};
  --lime: #{$wcag_green};
  --mint: #{$wcag_green};
  --important: #{$wcag_important};
  --orange: #{$wcag_yellow};
  --tiger: #{$wcag_yellow};
  --cheek: #{$wcag_red};
  --coral: #{$wcag_red};
  --link: #{$wcag_link};
  --tsuna: #{$wcag_spooky-ghost};
}

/** Display vacancy text */
.orangeColor {
  color: var(--tiger);
}
.greenColor {
  color: var(--lime);
}
.grayColor {
  color: var(--spooky-ghost);
}
.pinkColor {
  color: var(--cheek);
}

svg.orangeColor {
  fill: var(--tiger);
}
svg.greenColor {
  fill: var(--lime);
}
svg.grayColor {
  fill: var(--spooky-ghost);
}
svg.pinkColor {
  fill: var(--cheek);
}

/** map pins */
.white-label-18 {
  display: flex !important;
  flex-direction: column;
  justify-content: center !important;
  align-items: center !important;
  width: 44px;
  height: 44px;
  color: #fff;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
}
.white-label-18 .pin-waiting-num {
  font-size: 18px;
  font-weight: 500;
  line-height: 16.62px;
}
.white-label-18 .pin-waiting-word {
  font-size: 10px;
  font-weight: 500;
  line-height: 14.8px;
}
.white-label-22 {
  display: flex !important;
  flex-direction: column;
  justify-content: center !important;
  align-items: center !important;
  width: 54px;
  height: 54px;
  color: #fff;
  font-size: 18px;
  font-weight: 500;
  cursor: pointer;
}
.white-label-22 .pin-waiting-num {
  font-size: 22px;
  font-weight: 500;
  line-height: 23.56px;
}
.white-label-22 .pin-waiting-word {
  font-size: 12px;
  font-weight: 500;
  line-height: 17.76px;
}
</style>
